/* A clip somebody sent by link, for a person with no account here.
 *
 * Before the session check, like the Lusedagbok QR page: the person opening
 * this is a biologist or a supplier reading an email, and they have never
 * signed in and never will. The token in the path is the authorisation.
 *
 * The note, when it was recorded, the player and the level. No farm, no
 * creator, no navigation -- the receiver does not send them. The timeline is
 * drawn from the node's own per-second levels, so the loud part can be found
 * by eye before anything plays, and the figure moves to the sound itself, read
 * live off the analyser, so what it does is always what is being heard.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type SharedClip } from '../api'
import { Figur, type Drive } from '../components/Figur'
import { useLang, useT } from '../lang'
import { createSegmentPlayer, type SegmentPlayer } from '../segments'

/** Re-fetch rather than reuse once a signature is this old. URLs live 300 s. */
const FRESH_MS = 200_000
const LOW_HZ = 300
const HIGH_HZ = 3000

function mmss(s: number): string {
  const v = Math.max(0, Math.floor(s))
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`
}

async function fetchShared(token: string, count: boolean): Promise<SharedClip> {
  const r = await fetch(
    `/v1/clips/shared/${encodeURIComponent(token)}${count ? '' : '?count=false'}`,
    { headers: { Accept: 'application/json' }, credentials: 'omit' })
  if (!r.ok) {
    const body = await r.json().catch(() => null)
    throw Object.assign(new Error(body?.detail?.detail ?? String(r.status)),
      { status: r.status })
  }
  return r.json()
}

/** Follows a signal's own range, so the figure moves as much in a quiet fjord
 *  as next to a well boat. The ceiling falls slowly and the floor rises
 *  slowly, so a loud moment still reads as loud against the seconds before. */
function agc() {
  let lo = NaN, hi = NaN
  return (x: number, dt: number) => {
    if (!Number.isFinite(x)) return 0
    if (Number.isNaN(lo)) { lo = x - 6; hi = x + 6 }
    hi = x > hi ? x : hi - 4 * dt
    lo = x < lo ? x : lo + 2 * dt
    if (hi - lo < 14) lo = hi - 14
    return Math.max(0, Math.min(1, (x - lo) / (hi - lo)))
  }
}

const power = (db: number) => Math.pow(10, db / 10)

/* The level scale, in dB re 1 µPa, and the one place its colours live.
 *
 * Green up to 110 dB -- an ordinary sea -- then gradually towards yellow up
 * to 135, then three steps that are deliberately NOT a gradient: 135, 157 and 175 are thresholds somebody acts
 * on, and a smooth ramp would hide exactly where they are crossed. Each step
 * is also darker or lighter than its neighbour, not only another hue. */
export const DB_BREAKS = [135, 157, 175] as const
const CALM_DB = 110
const MINT = [180, 217, 199], GREEN = [76, 196, 128], YELLOW = [245, 213, 71]
/** Green to yellow, 0..1. */
function rampRgb(f: number): number[] {
  const x = Math.max(0, Math.min(1, f))
  return GREEN.map((m, i) => Math.round(m + (YELLOW[i]! - m) * x))
}
function dbRgb(db: number): number[] {
  if (db >= 175) return [210, 60, 99]
  if (db >= 157) return [236, 106, 76]
  if (db >= 135) return [243, 156, 61]
  if (db < CALM_DB) return GREEN
  return rampRgb((db - CALM_DB) / (135 - CALM_DB))
}
const rgb = (c: number[], a = 1) => a >= 1 ? `rgb(${c.join(',')})` : `rgba(${c.join(',')},${a})`

/** Builds the timeline from the audio itself, for a clip whose hydrophone has
 *  not sent its levels yet -- the node uploads them once an hour, so a clip
 *  recorded a minute ago has none. dBFS of the FILE, so it pairs with the
 *  clip's own offset (capture gain already taken out), not the node's. */
async function measureFile(
  doc: SharedClip, n: number, onSlice: (db: (number | null)[], done: boolean) => void,
  isAlive: () => boolean,
) {
  const dur = doc.duration_s
  const acc = new Float64Array(n), cnt = new Float64Array(n)
  const dec = new OfflineAudioContext(1, 1, 48000)
  const slices = () => Array.from(acc, (a, k) =>
    cnt[k] ? Math.round(10 * Math.log10(a / cnt[k]! + 1e-20) * 10) / 10 : null)
  for (const seg of doc.segments) {
    if (!isAlive()) return
    const res = await fetch(seg.url)
    if (!res.ok) continue
    let buf: AudioBuffer
    try { buf = await dec.decodeAudioData(await res.arrayBuffer()) } catch { continue }
    const x = buf.getChannelData(0), sr = buf.sampleRate
    for (let j = 0; j < x.length; j++) {
      const t = seg.offset_s + j / sr
      if (t < 0 || t >= dur) continue
      const k = Math.min(n - 1, Math.floor((t / dur) * n))
      acc[k]! += x[j]! * x[j]!
      cnt[k]!++
    }
    onSlice(slices(), false)
  }
  if (isAlive()) onSlice(slices(), true)
}

export function Delt({ token }: { token: string }) {
  const t = useT()
  const { lang } = useLang()
  const [doc, setDoc] = useState<SharedClip | null>(null)
  const [err, setErr] = useState<number | null>(null)
  const [at, setAt] = useState(0)
  const [state, setState] = useState<'idle' | 'buffering' | 'playing' | 'paused' | 'ended'>('idle')
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null)
  const [live, setLive] = useState<{ db: number; level: number; meter: number; low: number; mid: number; high: number } | null>(null)

  const latest = useRef<{ doc: SharedClip; at: number } | null>(null)
  const player = useRef<SegmentPlayer | null>(null)
  const ctx = useRef<AudioContext | null>(null)
  const drive = useRef<Drive>({ level: 0, low: 0, mid: 0, high: 0 })
  const ring = useRef<HTMLDivElement | null>(null)
  const [wide, setWide] = useState(() => window.matchMedia('(min-width: 900px)').matches)
  const [fileTl, setFileTl] = useState<{ db: (number | null)[]; done: boolean } | null>(null)

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 900px)')
    const on = () => setWide(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])

  useEffect(() => {
    let alive = true
    fetchShared(token, true)
      .then((d) => {
        if (!alive) return
        latest.current = { doc: d, at: Date.now() }
        setDoc(d)
        if (d.note) document.title = d.note
      })
      .catch((e) => { if (alive) setErr((e as { status?: number }).status ?? 0) })
    return () => { alive = false }
  }, [token])

  /* No levels from the node for this span, or only some of them: measure the
   * file. The node uploads its levels once an hour, so a clip that reaches
   * into an hour not yet uploaded -- or crosses a clipped minute the receiver
   * cannot rescale -- has holes, and a timeline with a blank first minute
   * reads as silence. The whole clip is measured rather than the holes
   * patched, so every bar is on one scale. Bounded, so a six-hour mark does
   * not pull gigabytes into a phone just to draw bars. */
  useEffect(() => {
    const node = doc?.timeline?.db
    const whole = !!node?.length && node.every((v) => v != null)
    if (!doc || whole || !doc.segments.length) return
    const bytes = doc.segments.reduce((a, sg) => a + (sg.size ?? 0), 0)
    if (doc.segments.length > 40 || bytes > 150 * 1024 * 1024) return
    let alive = true
    const n = Math.max(8, Math.min(120, Math.floor(doc.duration_s)))
    void measureFile(doc, n, (db, done) => { if (alive) setFileTl({ db, done }) }, () => alive)
      .catch(() => {})
    return () => { alive = false }
  }, [doc])

  useEffect(() => () => {
    player.current?.destroy()
    void ctx.current?.close()
  }, [])

  const sign = useCallback(async (keys: string[]) => {
    let cur = latest.current
    if (!cur || Date.now() - cur.at > FRESH_MS) {
      const d = await fetchShared(token, false)
      cur = latest.current = { doc: d, at: Date.now() }
    }
    const byKey = new Map(cur.doc.segments.map((s) => [String(s.i), s.url]))
    return {
      urls: keys.filter((k) => byKey.has(k)).map((k) => ({ key: k, url: byKey.get(k)! })),
      rejected: keys.filter((k) => !byKey.has(k)).map((k) => ({ key: k })),
    }
  }, [token])

  const dur = doc?.duration_s ?? 0
  const playing = state === 'playing' || state === 'buffering'

  const cal = doc?.level.offset_db ?? null

  /* The ear. Reads the analyser every frame while playing: broadband from the
   * waveform, three bands from the spectrum, each through its own AGC into
   * 0..1 for the figure. The number on screen is updated a few times a second,
   * not every frame -- a figure that flickers at 60 Hz is not readable. */
  useEffect(() => {
    if (!analyser || !playing) {
      drive.current = { level: 0, low: 0, mid: 0, high: 0 }
      if (ring.current) ring.current.style.opacity = '0'
      return
    }
    const freq = new Float32Array(analyser.frequencyBinCount)
    const wave = new Float32Array(analyser.fftSize)
    const hz = analyser.context.sampleRate / analyser.fftSize
    const lowEnd = Math.max(2, Math.floor(LOW_HZ / hz))
    const midEnd = Math.floor(HIGH_HZ / hz)
    const g = { level: agc(), low: agc(), mid: agc(), high: agc() }
    let raf = 0, last = performance.now(), shown = 0, odd = false
    let bands = { low: -120, mid: -120, high: -120 }
    const tick = () => {
      raf = requestAnimationFrame(tick)
      const now = performance.now()
      const dt = Math.min(.1, (now - last) / 1000)
      last = now
      analyser.getFloatTimeDomainData(wave)
      let sum = 0
      for (let i = 0; i < wave.length; i++) sum += wave[i]! * wave[i]!
      const dbfs = 10 * Math.log10(sum / wave.length + 1e-20)
      // The spectrum is 16k bins; every other frame is plenty for motion.
      odd = !odd
      if (odd) {
        analyser.getFloatFrequencyData(freq)
        let l = 0, m = 0, h = 0
        for (let i = 1; i < freq.length; i++) {
          const p = power(freq[i]!)
          if (i < lowEnd) l += p
          else if (i < midEnd) m += p
          else h += p
        }
        bands = { low: 10 * Math.log10(l + 1e-20), mid: 10 * Math.log10(m + 1e-20), high: 10 * Math.log10(h + 1e-20) }
      }
      /* How much the figure moves. On a calibrated hydrophone it is the real
       * level: still below 110 dB re 1 µPa -- an ordinary sea is not an
       * event -- lively by 135, and at 157 and up the coral and the wide
       * eyes, which is where the timeline turns red. The bands follow their
       * own AGC for character, but only as far as the level lets them, so a
       * quiet minute stays quiet whatever its spectrum. Uncalibrated, there
       * is no real level to hold it to, and the AGC alone has to do. */
      const abs = cal == null ? null : dbfs + cal
      const level = abs == null ? g.level(dbfs, dt)
        : Math.max(0, Math.min(1, (abs - 110) / (175 - 110)))
      const gate = abs == null ? 1 : Math.max(0, Math.min(1, (abs - 108) / 22))
      const d = {
        level, low: g.low(bands.low, dt) * gate,
        mid: g.mid(bands.mid, dt) * gate, high: g.high(bands.high, dt) * gate,
      }
      drive.current = d
      if (ring.current) {
        ring.current.style.transform = `scale(${(1 + d.level * .55).toFixed(3)})`
        ring.current.style.opacity = (.15 + .55 * d.level).toFixed(2)
      }
      if (now - shown > 140) {
        shown = now
        setLive({ db: dbfs, ...d,
          meter: abs == null ? d.level : Math.max(0, Math.min(1, (abs - 90) / (175 - 90))) })
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [analyser, playing, cal])

  /* Built on the first press, inside the gesture -- a browser only lets an
   * AudioContext start from one. On the clip's own clock: second 0 is where
   * the clip starts, and a mark's first segment may begin before it. */
  const ensurePlayer = () => {
    if (player.current || !doc) return player.current
    ctx.current = new AudioContext()
    player.current = createSegmentPlayer({
      missionId: '',
      segments: doc.segments.map((s) => ({
        key: String(s.i), startUtc: s.offset_s, durationS: s.duration_s,
        size: s.size, kind: 'audio',
      })),
      context: ctx.current,
      sign,
      onTime: (e) => setAt(e),
      onState: (s) => setState(s),
      onAnalyser: (a) => setAnalyser(a),
    })
    player.current.seek(0)
    return player.current
  }

  // Stop where the clip stops; a mark's last segment runs on past it.
  useEffect(() => {
    if (state === 'playing' && at >= dur && dur > 0) {
      player.current?.pause()
      setState('ended')
      setAt(dur)
    }
  }, [at, dur, state])

  const toggle = () => {
    const p = ensurePlayer()
    if (!p) return
    if (playing) { p.pause(); return }
    if (state === 'ended' || at >= dur) { p.seek(0); setAt(0) }
    void ctx.current?.resume()
    void p.play()
  }

  const seek = (v: number) => {
    const s = Math.max(0, Math.min(dur, v))
    const p = ensurePlayer()
    setAt(s)
    p?.seek(s)
    if (state === 'ended') setState('paused')
  }

  /* The timeline, shaped once, from the node's levels or, failing those, the
   * file's. Both are dBFS with their own offset to dB re 1 µPa. */
  const tl = useMemo(() => {
    // The node's levels while they are whole, or while the file is still
    // being measured; the file's once it is done, or when there is nothing
    // else to draw.
    const node = doc?.timeline?.db?.length ? doc.timeline : null
    const fromNode = node && (node.db.every((v) => v != null) || !fileTl?.done) ? node : null
    const raw = fromNode ? fromNode.db : fileTl?.db
    if (!doc || !raw?.length) return null
    const off = fromNode ? fromNode.offset_db : doc.level.offset_db
    const vals = raw.filter((v): v is number => v != null)
    if (!vals.length) return null
    const abs = (v: number) => v + (off ?? 0)
    const hi = abs(Math.max(...vals)), lo = abs(Math.min(...vals))
    // Headroom above the peak, and the floor a little under the quietest
    // slice, so the quietest part still draws as a bar and not as nothing.
    const top = hi + Math.max(2, (hi - lo) * .06)
    const floor = lo - Math.max(3, (hi - lo) * .2)
    const pct = (d: number) => Math.max(0, Math.min(100, ((d - floor) / (top - floor)) * 100))
    const peak = raw.indexOf(Math.max(...vals))
    return {
      n: raw.length, hi, lo, peak, calibrated: off != null,
      // The node's uncalibrated dBFS and the file's are not on one scale (the
      // file carries its capture gain); only a calibrated or file-measured
      // timeline may stand in for the live number while paused.
      paired: off != null || !fromNode,
      breaks: off != null ? DB_BREAKS.filter((x) => x > floor && x < top).map((x) => ({ db: x, at: pct(x) })) : [],
      ticks: [top, (top + floor) / 2, floor].map((x) => ({ db: Math.round(x), at: pct(x) })),
      bars: raw.map((v) => {
        if (v == null) return { h: 0, d: null as number | null, c: MINT }
        const d = abs(v)
        return { h: Math.max(3, pct(d)), d,
                 c: off != null ? dbRgb(d) : rampRgb((d - lo) / Math.max(1, hi - lo)) }
      }),
    }
  }, [doc, fileTl])

  if (err != null) return <Gone kind={err === 410 ? 'gone' : 'missing'} />

  if (!doc) {
    return (
      <div className="dp">
        <main className="dp-main dp-center"><p className="dp-lead">{t('Loading')}</p></main>
      </div>
    )
  }

  const offset = doc.level.offset_db
  const calibrated = offset != null
  const unit = calibrated ? 'dB' : 'dBFS'
  const frac = dur > 0 ? Math.min(1, Math.max(0, at / dur)) : 0
  /* Clock time in the viewer's own zone, which is what "when was this" means
   * to whoever is listening. The clip's length when an older receiver did
   * not say when it started. */
  const t0 = doc.started_utc ? Date.parse(doc.started_utc) : NaN
  const clockAt = (sec: number) => Number.isFinite(t0)
    ? new Date(t0 + sec * 1000).toLocaleTimeString(lang === 'nb' ? 'nb-NO' : 'en-GB',
        { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : mmss(sec)
  const dayLabel = Number.isFinite(t0)
    ? new Date(t0).toLocaleDateString(lang === 'nb' ? 'nb-NO' : 'en-GB',
        { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : null
  const idx = tl ? Math.min(tl.n - 1, Math.floor(frac * tl.n)) : 0
  const here = tl?.bars[idx]

  /* The number: live while playing, the timeline's figure for the playhead
   * while paused (when the two are on one scale). */
  const nowDb = playing && live
    ? live.db + (offset ?? 0)
    : tl?.paired && here?.d != null ? here.d : null
  const colour = (d: number | null) => d == null ? MINT
    : calibrated ? dbRgb(d) : tl ? rampRgb((d - tl.lo) / Math.max(1, tl.hi - tl.lo)) : MINT
  const nowRgb = colour(nowDb)
  const meter = playing && live ? live.meter : here ? here.h / 100 : 0
  const bandsNow = playing && live ? live : { low: 0, mid: 0, high: 0 }
  const peakPct = tl ? Math.max(12, Math.min(88, ((tl.peak + .5) / tl.n) * 100)) : 0

  return (
    <div className="dp">
      <div className="dp-sea dp-sea1" aria-hidden="true" />
      <div className="dp-sea dp-sea2" aria-hidden="true" />
      <main className="dp-main">
        <header className="dp-head">
          <div className="dp-fig">
            <div ref={ring} className="dp-ring" style={{ borderColor: rgb(nowRgb) }} aria-hidden="true" />
            <Figur size={wide ? 150 : 88} drive={drive} label={t('The figure listens along')} />
          </div>
          <div className="dp-titles">
            <h1 className="dp-title">{doc.note || t('Sound clip')}</h1>
            {dayLabel && <span className="dp-day">{dayLabel}</span>}
          </div>
        </header>

        <section className="dp-now">
          <div className="dp-read">
          <span className="dp-cap">{t('Sound level now')}</span>
          <div className="dp-num">
            <span className="dp-big" style={{ color: rgb(nowRgb) }}>
              {nowDb == null ? '—' : Math.round(nowDb)}
            </span>
            <span className="dp-unit">
              <span>{unit}</span>
              {calibrated && <span className="dp-ref">re 1 µPa{doc.level.estimated ? ' (est.)' : ''}</span>}
            </span>
          </div>
          </div>
          <div className="dp-side">
          <div className="dp-meter"><div
            style={{ width: `${Math.max(3, meter * 100)}%`, background: rgb(nowRgb) }} /></div>
          <div className="dp-bands" aria-hidden="true">
            {([['Low', bandsNow.low], ['Mid', bandsNow.mid], ['High', bandsNow.high]] as const).map(([k, v]) => (
              <div key={k} className="dp-band">
                <div><span style={{ height: `${8 + v * 92}%`, background: rgb(rampRgb(v)) }} /></div>
                <span>{t(k)}</span>
              </div>
            ))}
          </div>
          </div>
        </section>

        {doc.segments.length === 0 ? (
          <p className="dp-lead">{t('That recording is no longer in storage.')}</p>
        ) : (
          <section className="dp-time">
            {tl && (
              <div className="dp-peakrow">
                <button type="button" className="dp-peak"
                  style={{ left: `${peakPct}%`, background: rgb(colour(tl.hi)) }}
                  onClick={() => seek((tl.peak / tl.n) * dur)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></svg>
                  {t('Loudest')} {Math.round(tl.hi)} {unit}
                </button>
              </div>
            )}
            <div className="dp-trackrow">
              {tl && (
                <div className="dp-axis" aria-hidden="true">
                  {tl.ticks.map((k) => <span key={k.db} style={{ bottom: `${k.at}%` }}>{k.db}</span>)}
                </div>
              )}
              <div className={`dp-wave${tl ? '' : ' flat'}`}>
                {tl && tl.breaks.map((b) => (
                  <span key={b.db} className="dp-break" style={{ bottom: `${b.at}%`, borderColor: rgb(dbRgb(b.db), .7) }}>
                    <em style={{ color: rgb(dbRgb(b.db)) }}>{b.db}</em>
                  </span>
                ))}
                {tl ? tl.bars.map((b, i) => (
                  <span key={i} className="dp-bar" style={{
                    height: `${b.h}%`,
                    background: rgb(b.c, (i + .5) / tl.n <= frac ? 1 : .55),
                  }} />
                )) : <span className="dp-fill" style={{ width: `${frac * 100}%` }} />}
                <span className="dp-head-line" style={{ left: `${frac * 100}%` }} aria-hidden="true" />
                <input type="range" className="dp-scrub" min={0} max={dur} step={0.1}
                  value={Math.min(at, dur)} aria-label={t('Position')}
                  aria-valuetext={clockAt(at)}
                  onChange={(e) => seek(Number(e.target.value))} />
              </div>
            </div>
            <div className="dp-times">
              <span>{clockAt(Math.min(at, dur))}</span>
              <span>{clockAt(dur)}</span>
            </div>
          </section>
        )}

        {doc.segments.length > 0 && (
          <div className="dp-ctl">
            <button type="button" className="dp-skip" onClick={() => seek(at - 10)}
              aria-label={t('10 seconds back')}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 3v5h5" /></svg>
            </button>
            <button type="button" className="dp-play" onClick={toggle}
              aria-label={playing ? t('Pause') : t('Play')}>
              {playing
                ? <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1.5" /><rect x="14" y="5" width="4" height="14" rx="1.5" /></svg>
                : <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.2-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" /></svg>}
            </button>
            <button type="button" className="dp-skip" onClick={() => seek(at + 10)}
              aria-label={t('10 seconds forward')}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" /></svg>
            </button>
          </div>
        )}
        {/* Under the controls: the scale explains the timeline, but Play is
            what a phone must show without scrolling. */}
        {tl?.calibrated && (
          <ul className="dp-legend" aria-label={t('Colour scale')}>
            <li><i style={{ background: rgb(GREEN) }} />{t('under')} 110 dB</li>
            <li><i style={{ background: `linear-gradient(90deg, ${rgb(GREEN)}, ${rgb(YELLOW)})` }} />110–135</li>
            <li><i style={{ background: rgb(dbRgb(140)) }} />135–157</li>
            <li><i style={{ background: rgb(dbRgb(160)) }} />157–175</li>
            <li><i style={{ background: rgb(dbRgb(180)) }} />{t('over')} 175</li>
          </ul>
        )}
      </main>
      <Foot />
    </div>
  )
}

function Foot() {
  const t = useT()
  return (
    <footer className="dp-foot">
      <span>{t('from')}</span>
      <img src="/phoniq-ordmerke.svg" width={62} height={21} alt="Phoniq" />
    </footer>
  )
}

/** A withdrawn, expired or mistyped link. The figure stays, because the page
 *  is still somebody's first sight of the product. */
function Gone({ kind }: { kind: 'gone' | 'missing' }) {
  const t = useT()
  const still = useRef<Drive>({ level: 0, low: 0, mid: 0, high: 0 })
  return (
    <div className="dp">
      <div className="dp-sea dp-sea1" aria-hidden="true" />
      <div className="dp-sea dp-sea2" aria-hidden="true" />
      <main className="dp-main dp-center">
        <Figur size={180} drive={still} label="" />
        <div className="dp-silent" aria-hidden="true">
          {Array.from({ length: 9 }, (_, i) => <span key={i} />)}
        </div>
        <h1 className="dp-title">{kind === 'gone' ? t('Quiet here now') : t('Link not found')}</h1>
        <p className="dp-lead">
          {kind === 'gone'
            ? t('This link no longer works. The person who sent it has withdrawn it, or it has expired.')
            : t('Check that the whole address was copied.')}
        </p>
        {kind === 'gone' && (
          <p className="dp-note">{t('Ask the person who sent you the clip for a new link.')}</p>
        )}
      </main>
      <Foot />
    </div>
  )
}
